import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Shift } from '../interfaces';
import { Property } from '../Toolbar';
import { formatPrice } from '../utils/formatPrice';
import './XReportPopup.css';

interface XReportPopupProps {
    isOpen: boolean;
    onClose: () => void;
    shift: Shift | null;
    property: Property | null;
}

const XReportPopup: React.FC<XReportPopupProps> = ({ isOpen, onClose, shift, property }) => {
    const receiptRef = useRef<HTMLDivElement>(null);

    if (!isOpen || !shift) return null;

    const formatCurrency = (value: number | null | undefined) => {
        return formatPrice(value);
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toISOString().slice(0, 16).replace('T', ' ');
    };

    const formatShiftId = (id: string | null | undefined) => {
        if (!id) return '000000';
        return id.toString().padStart(6, '0');
    };

    const formatDateForFilename = () => {
        const now = new Date();
        return now.toISOString().slice(0, 10);
    };

    // Calculate cash sales (totalRevenue - creditCards - roomCharges)
    const cashSales = (shift.totalRevenue ?? 0) - (shift.creditCards ?? 0) - (shift.roomCharges ?? 0);

    const handlePrint = () => {
        window.print();
    };

    const handleSave = async () => {
        if (!receiptRef.current) return;

        try {
            const canvas = await html2canvas(receiptRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

            const imgX = (pdfWidth - imgWidth * ratio) / 2;
            const imgY = 10;

            pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

            const filename = `X-Report_${formatShiftId(shift.shiftId)}_${formatDateForFilename()}.pdf`;
            pdf.save(filename);
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    return (
        <>
            <div className="xreport-overlay" onClick={onClose}></div>
            <div className="xreport-popup">
                <div className="xreport-header">
                    <div className="xreport-header-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16 13H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16 17H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10 9H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <span className="xreport-header-title">X-REPORT PREVIEW</span>
                    <button className="xreport-close-btn" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>

                <div className="xreport-content">
                    <div className="xreport-receipt" ref={receiptRef}>
                        {/* Company Header */}
                        <div className="receipt-company">
                            <div className="company-name">{property?.displayName || 'PROPERTY NAME'}</div>
                            <div className="company-details">
                                {/* {property?.address && <span>{property.address}</span>} */}
                            </div>
                            <div className="company-details">Cashier: {shift.startedByUserWithName || 'Unknown'}</div>
                        </div>

                        {/* Report Title */}
                        <div className="receipt-title">
                            <div className="title-main">X REPORT (NO RESET)</div>
                            <div className="title-details">X# {formatShiftId(shift.shiftId)} &nbsp; {formatDate(new Date().toISOString())}</div>
                        </div>

                        <div className="receipt-divider"></div>

                        <div className="receipt-since">
                            SINCE: Last Z at {formatDate(shift.startDate)}
                        </div>

                        <div className="receipt-divider"></div>

                        {/* Sales Section */}
                        <div className="receipt-section">
                            <div className="section-title">SALES</div>
                            <div className="receipt-line">
                                <span>Gross Sales (excl tax)</span>
                                <span>{formatCurrency(shift.grossSales)}</span>
                            </div>
                            <div className="receipt-line">
                                <span>Discounts/Promos</span>
                                <span>{shift.discounts ? `-${formatCurrency(shift.discounts)}` : '0.00'}</span>
                            </div>
                            {(shift as any).mealPlanDeductions > 0 && (
                                <div className="receipt-line">
                                    <span>Meal Plan Deductions</span>
                                    <span>-{formatCurrency((shift as any).mealPlanDeductions)}</span>
                                </div>
                            )}
                            <div className="receipt-line">
                                <span>Returns/Refunds</span>
                                <span>{shift.returns ? `-${formatCurrency(shift.returns)}` : '0.00'}</span>
                            </div>
                            <div className="receipt-line">
                                <span>Net Sales</span>
                                <span>{formatCurrency(shift.netSales)}</span>
                            </div>
                            <div className="receipt-line">
                                <span>Tax</span>
                                <span>{formatCurrency(shift.taxes)}</span>
                            </div>
                            <div className="receipt-line">
                                <span>Service Charge</span>
                                <span>{formatCurrency(shift.serviceCharge)}</span>
                            </div>
                            <div className="receipt-line bold">
                                <span>Total Revenue</span>
                                <span>{formatCurrency(shift.totalRevenue)}</span>
                            </div>
                            <div className="receipt-line muted">
                                <span>Total Receipts</span>
                                <span>{shift.numberOfReceipts ?? 0}</span>
                            </div>
                        </div>

                        {/* Floor Breakdown */}
                        {(shift as any)?.floorBreakdown?.length > 0 && (
                            <>
                                <div className="receipt-divider"></div>
                                <div className="receipt-section">
                                    <div className="section-title">SALES BY FLOOR</div>
                                    {(shift as any).floorBreakdown.map((floor: any, idx: number) => (
                                        <div key={idx} style={{ marginBottom: 4 }}>
                                            <div className="receipt-line bold">
                                                <span>{floor.floorName}</span>
                                                <span>{formatCurrency(floor.totalRevenue)}</span>
                                            </div>
                                            <div className="receipt-line muted">
                                                <span>Orders: {floor.orderCount} | Gross: {formatCurrency(floor.grossSales)}</span>
                                            </div>
                                            {floor.mealPlanDeductions > 0 && (
                                                <div className="receipt-line muted">
                                                    <span>Meal Plan: -{formatCurrency(floor.mealPlanDeductions)}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        <div className="receipt-divider"></div>

                        {/* Tenders Section */}
                        <div className="receipt-section">
                            <div className="section-title">TENDERS</div>
                            <div className="receipt-line">
                                <span>Cash</span>
                                <span>{formatCurrency(cashSales)}</span>
                            </div>
                            <div className="receipt-line">
                                <span>Credit Card</span>
                                <span>{formatCurrency(shift.creditCards)}</span>
                            </div>
                            <div className="receipt-line">
                                <span>Room Charges</span>
                                <span>{formatCurrency(shift.roomCharges)}</span>
                            </div>
                        </div>

                        <div className="receipt-divider"></div>

                        {/* Cash Snapshot Section */}
                        <div className="receipt-section">
                            <div className="section-title">CASH SNAPSHOT</div>
                            <div className="receipt-line">
                                <span>Cash Sales</span>
                                <span>{formatCurrency(cashSales)}</span>
                            </div>
                            <div className="receipt-line bold">
                                <span>Expected Cash</span>
                                <span>{formatCurrency(shift.expectedCash)}</span>
                            </div>
                        </div>

                        <div className="receipt-divider"></div>

                        {/* Controls Section */}
                        <div className="receipt-section">
                            <div className="section-title">CONTROLS</div>
                            <div className="receipt-center">
                                Receipts: {shift.numberOfReceipts ?? 0} &nbsp; Voids: {shift.numberOfVoidOrders ?? 0}
                            </div>
                            <div className="receipt-center">
                                First: {shift.firstOrderNumber || '-'}
                            </div>
                            <div className="receipt-center">
                                Last: {shift.lastOrderNumber || '-'}
                            </div>
                        </div>

                        <div className="receipt-divider"></div>

                        {/* Footer */}
                        <div className="receipt-footer">
                            *INFORMATIONAL ONLY. COUNTERS NOT RESET.*
                        </div>
                    </div>
                </div>

                <div className="xreport-actions">
                    <button className="xreport-btn save" onClick={handleSave}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Save
                    </button>
                    <button className="xreport-btn print" onClick={handlePrint}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6 18H4C2.89543 18 2 17.1046 2 16V11C2 9.89543 2.89543 9 4 9H20C21.1046 9 22 9.89543 22 11V16C22 17.1046 21.1046 18 20 18H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Print
                    </button>
                </div>
            </div>
        </>
    );
};

export default XReportPopup;